import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { ExclusiveChoiceDropdown, OutlinedButton } from "dash-styled"
import { metaAI } from "neql"
import { useAppTemplate } from "../../../../../../../Hooks/useAppTemplate"
import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { Result } from "../../../../../../../Models/Utility/Result"
import { AppDetailBase } from "../../AppDetailBase/AppDetailBase"
import { useNeqlContextProvider } from "../../../../../../../Hooks/useNeqlContextProvider"
import { Table, TableColumn } from "resha"
import {
    AIDataset,
    AIDatasetWithMetrics,
    AIMetric
} from "../../../../../../../Models/API/AIModel"
import { unique } from "../../../../../../../Helpers/Functions/array"
import { format } from "date-fns"

function getMetricId(metric: AIMetric): string {
    return String(metric.id)
}

function renderDataset(dataset: AIDataset): string {
    const formattedDate = format(new Date(dataset.created_at), "dd.MM.yyyy")
    return (dataset.metadata as any)?.count === undefined
        ? `Dataset created ${formattedDate}`
        : `Dataset created ${formattedDate}, ${
              (dataset.metadata as any).count
          } datapoints`
}

export interface AIModelsComparisonProps {
    orgUsername: string
    appID: string
    datasetID?: string
}

/**
 * No longer used, kept for future reuse
 */
export const AIModelsComparison: FC<AIModelsComparisonProps> = ({
    orgUsername,
    appID,
    datasetID
}) => {
    const clientForApp = useNeqlContextProvider("metaAIApp", appID)
    const templateFetch = useAppTemplate(appID)

    //TODO: refactor to reuse
    const taskNamesFetch = useMemo(
        () =>
            !hasValue(templateFetch)
                ? templateFetch
                : Result.ok(
                      Object.keys(
                          templateFetch.value.defaultConstraints
                              .taskConstraints ?? {}
                      )
                  ),
        [templateFetch]
    )

    const { data: fullDatasetList } = metaAI.useGetDatasetsQuery({
        client: clientForApp,
        variables: {
            appId: appID
        }
    })

    const [selectedTask, setSelectedTask] = useState<string | undefined>(() =>
        datasetID && fullDatasetList
            ? (fullDatasetList?.meta_ai_dataset || []).find(
                  dataset => dataset.id === datasetID
              )?.task_name ?? undefined
            : getValue(taskNamesFetch)?.[0]
    )

    const [selectedMetricId, setSelectedMetricId] =
        useState<string | undefined>()

    const datasetList = useMemo(
        () =>
            (fullDatasetList?.meta_ai_dataset || []).filter(
                dataset => dataset.task_name === selectedTask
            ),
        [fullDatasetList, selectedTask]
    )

    const [selectedDataset, setSelectedDataset] = useState<
        AIDataset | undefined
    >(
        datasetID
            ? datasetList.find(dataset => dataset.id === datasetID)
            : datasetList[0]
    )

    // set selected task after datasets and tasks are loaded
    useEffect(() => {
        if (
            selectedTask ||
            !hasValue(taskNamesFetch) ||
            taskNamesFetch.value.length === 0
        ) {
            return
        }

        if (datasetID) {
            setSelectedTask(
                datasetList.find(dataset => dataset.id === datasetID)
                    ?.task_name ?? undefined
            )
        } else {
            setSelectedTask(taskNamesFetch.value[0])
        }
    }, [selectedTask, taskNamesFetch, datasetID, datasetList])

    // set selected dataset after datasets and tasks are loaded
    useEffect(() => {
        if (!selectedDataset && datasetList.length > 0) {
            setSelectedDataset(
                datasetID
                    ? datasetList.find(dataset => dataset.id === datasetID)
                    : datasetList[0]
            )
        }
    }, [selectedDataset, datasetList, datasetID])

    const { data: datasetWithMetrics } = metaAI.useGetDatasetWithMetricsQuery({
        client: clientForApp,
        skip: selectedDataset === undefined,
        variables: selectedDataset
            ? {
                  datasetId: selectedDataset.id
              }
            : undefined
    })

    const columns: TableColumn<AIDatasetWithMetrics["metrics"][number]>[] =
        useMemo(() => {
            if (!datasetWithMetrics?.meta_ai_dataset_by_pk) {
                return []
            }
            const metricNames = unique(
                datasetWithMetrics.meta_ai_dataset_by_pk?.metrics.flatMap(
                    metric => Object.keys(metric.metric as any)
                )
            )
            return [
                {
                    renderHeader: () => "Model",
                    render: item =>
                        item.model
                            ? `${item.model?.name} (version ${item.model?.version})`
                            : item.model_id
                },
                ...metricNames.map<
                    TableColumn<AIDatasetWithMetrics["metrics"][number]>
                >(metricName => ({
                    renderHeader: () => metricName,
                    render: item => (item.metric as any)[metricName]
                }))
            ]
        }, [datasetWithMetrics])

    const checkedMetricIds = useMemo(
        () => (selectedMetricId === undefined ? [] : [selectedMetricId]),
        [selectedMetricId]
    )

    const metrics = datasetWithMetrics?.meta_ai_dataset_by_pk?.metrics

    const handleToggleMetric = useCallback((metric: AIMetric) => {
        setSelectedMetricId(prevSelectedMetricId =>
            prevSelectedMetricId === getMetricId(metric)
                ? undefined
                : getMetricId(metric)
        )
    }, [])

    const handleSelectDataset = useCallback(
        (dataset: AIDataset) => {
            if (selectedDataset !== dataset) {
                setSelectedDataset(dataset)
                setSelectedMetricId(undefined)
            }
        },
        [selectedDataset]
    )

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="Models Comparison"
            breadcrumbs={[]}
            fixedContentWidth={false}
        >
            <div className="flex gap-2 items-end">
                <ExclusiveChoiceDropdown
                    label="Task"
                    choices={
                        hasValue(taskNamesFetch) ? taskNamesFetch.value : []
                    }
                    value={selectedTask}
                    fitContentWidth={true}
                    size="sm"
                    onClick={setSelectedTask}
                />
                <ExclusiveChoiceDropdown
                    label="Dataset"
                    choices={datasetList}
                    value={selectedDataset}
                    fitContentWidth={true}
                    size="sm"
                    renderChoice={renderDataset}
                    onClick={handleSelectDataset}
                />
                {selectedMetricId ? (
                    <div className="flex-grow flex justify-end">
                        <OutlinedButton label="Download" />
                    </div>
                ) : null}
            </div>
            {metrics !== undefined && metrics.length === 0 ? (
                <div className="text-lg center text-center mt-8">
                    No metrics for selected dataset
                </div>
            ) : (
                <Table
                    columns={columns}
                    items={metrics}
                    getKey={getMetricId}
                    checkedItemIds={checkedMetricIds}
                    onToggleRowCheck={handleToggleMetric}
                    renderEmpty={() => ""}
                />
            )}
        </AppDetailBase>
    )
}
